import Auth from 'opensooq-sso';
import { getCookies } from './cookies';

const TOKEN_STORAGE_KEY = 'newApp:auth-token';
const EXPIRY_MARGIN_SECONDS = 30;

let sharedTokenPromise: Promise<string> | null = null;

const hasNumericExp = (value: unknown): value is { exp: number } =>
  typeof value === 'object' && value !== null && 'exp' in value && typeof value.exp === 'number';

const getTokenExpiry = (token: string): number | null => {
  const payload = token.split('.')[1];

  if (!payload) {
    return null;
  }

  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded: unknown = JSON.parse(
      window.atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')),
    );

    return hasNumericExp(decoded) ? decoded.exp : null;
  } catch {
    return null;
  }
};

const isTokenUsable = (token: string): boolean => {
  const expiry = getTokenExpiry(token);

  return expiry !== null && expiry - EXPIRY_MARGIN_SECONDS > Date.now() / 1000;
};

export const readStoredToken = (): string | null => {
  try {
    const token = window.localStorage.getItem(TOKEN_STORAGE_KEY);

    return token && isTokenUsable(token) ? token : null;
  } catch {
    return null;
  }
};

const storeToken = (token: string): void => {
  try {
    window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch {}
};

const clearStoredToken = (): void => {
  try {
    window.localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {}
};

export const generateToken = async (): Promise<string> => {
  // const storedToken = readStoredToken();

  // if (storedToken) {
  //   return storedToken;
  // }

  if (!sharedTokenPromise) {
    const request: Promise<string> = Promise.resolve()
      .then(() => Auth.getInstance().getToken(true, true))
      .then((token: string) => {
        if (sharedTokenPromise !== request) {
          throw new Error('Token request belongs to an expired session');
        }
        storeToken(token);
        return token;
      })
      .finally(() => {
        if (sharedTokenPromise === request) sharedTokenPromise = null;
      });
    sharedTokenPromise = request;
  }

  return sharedTokenPromise;
};

// Drops an in-flight request without discarding a still-valid stored token. Keep this
// separate from clearSharedTokenPromise: merging them makes every App mount refetch the
// token, which is what the stored copy exists to avoid.
export const resetSharedTokenPromise = (): void => {
  sharedTokenPromise = null;
};

export const clearSharedTokenPromise = (): void => {
  resetSharedTokenPromise();
  clearStoredToken();
};

export const checkIfLogin = (): boolean => !!getCookies().ecountry;
